import React, { useEffect, useState } from "react";
import {
    Clock,
    TestTube,
    FileCheck,
    IndianRupee,
} from "lucide-react";
import { getDashboardData, getDashboarGraph, getDashboarSample } from "./service/dashboard-service";
import { useLoader } from "../../_helper/LoaderContext";
import Chart from "react-apexcharts";
import { formatDate } from '../../utils/formatDate';
import { useLab } from "../../_helper/PharmacyProvider";
import { useCurrency } from "../../_helper/currencyContext";
import { useCollection } from "../../_helper/dailyCollection";
import { Card, Table } from "reactstrap";
import SuggestionTable from "../SearchPatient/SuggestionTable";
import { patiantSuggestionService } from "../SearchPatient/services/search-patient";

export default function TopSuggestion() {
    const [data, setData] = useState({});
    const { setLoading } = useLoader();
    const [rawCategories, setRawCategories] = useState([]); // raw dates like "2025-12-19"
    const [categories, setCategories] = useState([]); // formatted dates like "19 Dec"
    const [sampleAmt, setSampleAmt] = useState([]);
    const [sampleDate, setSampleDate] = useState([]);
    const { currentLab } = useLab();
    const { getCurrencySymbol } = useCurrency();
    const [apiData, setApiData] = useState([]);
    const { daily, setDaily } = useCollection()
    const [suggestionsLoading, setSuggestionsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);

    useEffect(() => {
        getData();
        if (currentLab.hospital_id) {
            getSuggestion()
        }
    }, []);

    const getSuggestion = async () => {
        setSuggestionsLoading(true)
        try {
            const suggestionForm = {
                pageNo: 1,
                pageSize: 10,
                clinic_id: currentLab.hospital_id
            };
            const suggestion = await patiantSuggestionService(suggestionForm);
            setSuggestions(suggestion.data?.data?.suggestions || []);
        } catch (error) {
            console.error("Error fetching patient data:", error);
        } finally {
            setSuggestionsLoading(false)
        }
    }

    const getData = async () => {
        setLoading(true);
        try {
            const [res, response, sampleRes] = await Promise.all([
                getDashboardData(),
                getDashboarGraph(),
                getDashboarSample()
            ]);

            if (response.data.success) {
                const graphData = response.data.data || [];
                setApiData(graphData);

                // Extract unique raw dates and sort chronologically
                const uniqueRawDates = [...new Set(graphData.map(item => item.date))];
                uniqueRawDates.sort((a, b) => new Date(a) - new Date(b));

                setRawCategories(uniqueRawDates);
                setCategories(uniqueRawDates.map(date => formatDate(date)));

            }

            if (sampleRes.data.success) {
                const sampleData = sampleRes.data.data || [];
                setSampleAmt(sampleData.map(item => item.count));
                setSampleDate(sampleData.map(item => formatDate(item.date)));
            }

            if (res.data?.success) {
                setData(res.data?.data || {});
                localStorage.setItem('collectionAmount', JSON.stringify(res.data.data.collectionAmount));
                setDaily(res.data.data.collectionAmount)
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    // Group data by currency using raw date as key
    const groupedByCurrency = apiData.reduce((acc, item) => {
        const currency = item.currencyType || currentLab?.baseCurrency || "INR";
        if (!acc[currency]) acc[currency] = {};
        acc[currency][item.date] = item.amount;
        return acc;
    }, {});

    // Build series for each currency, aligned with rawCategories order
    const series = Object.keys(groupedByCurrency).map(currency => ({
        name: `Collection (${currency})`,
        data: rawCategories.map(rawDate => groupedByCurrency[currency][rawDate] ?? 0)
    }));

    // Today's total collection across all currencies (optional enhancement)
    const todayRawDate = new Date().toISOString().split('T')[0]; // "2025-12-19"
    const todayTotal = apiData
        .filter(item => item.date === todayRawDate)
        .reduce((sum, item) => sum + (item.amount || 0), 0);

    const chartOptions = {
        chart: {
            id: "collection-chart",
            toolbar: { show: false },
            zoom: { enabled: false }
        },
        stroke: {
            curve: "smooth",
            width: 3
        },
        xaxis: {
            categories: categories,
            title: { text: "Date" },
            labels: {
                rotate: -45,
                style: { fontSize: "12px" }
            }
        },
        yaxis: {
            title: { text: "Amount" },
            labels: {
                formatter: (value) => value?.toLocaleString()
            }
        },
        tooltip: {
            y: {
                formatter: function (value, { seriesIndex }) {
                    if (value === 0) return "0";
                    const currencyMatch = series[seriesIndex]?.name.match(/\((.*?)\)/);
                    const currency = currencyMatch ? currencyMatch[1] : currentLab?.baseCurrency;
                    return `${getCurrencySymbol(currency)}${value?.toLocaleString()}`;
                }
            }
        },
        legend: {
            position: "top",
            horizontalAlign: "left"
        },
        colors: ["#0d6efd", "#28a745", "#ffc107", "#dc3545", "#6f42c1"],
        dataLabels: { enabled: false },
        grid: {
            borderColor: "#e0e0e0",
            strokeDashArray: 4
        }
    };

    const chartOptionsSample = {
        chart: {
            id: "sample-collection-chart",
            toolbar: { show: false }
        },
        colors: ["#1b255b"],
        stroke: {
            curve: "smooth",
            width: 3
        },
        xaxis: {
            categories: sampleDate,
            title: { text: "Date" },
            labels: {
                rotate: -45,
                style: { fontSize: "12px" }
            }
        },
        yaxis: {
            title: { text: "Sample Count" },
            labels: {
                formatter: (value) => Math.round(value)
            }
        },
        tooltip: {
            y: {
                formatter: (value) => `${value} samples`
            }
        },
        dataLabels: { enabled: false },
        grid: {
            borderColor: "#e0e0e0",
            strokeDashArray: 4
        }
    };

    const seriesSample = [{ name: "Sample Collection", data: sampleAmt }];

    const totalReports = (data?.pendingReports || 0) + (data?.completedReports || 0);
    const completionRate = totalReports > 0 ? ((data?.completedReports || 0) / totalReports) * 100 : 0;

    return (
        <div>
            {/* Metrics */}
            <div className="row g-4">
                <div className="col-12">
                    <div className="row g-3">
                        {/* Today's Collection */}
                        <div className="col-md-3">
                            <div className="card shadow rounded-4" style={{ border: "2px solid #1b255b" }}>
                                <div className="card-body p-3">
                                    <h6 className="fw-semibold mb-3">Today's Collection</h6>
                                    <h4 className="fw-bold text-success">
                                        {getCurrencySymbol(currentLab?.baseCurrency)}{data?.todayCollection?.toFixed(2) || 0}
                                    </h4>
                                    <p className="small text-muted mb-0">
                                        {data?.todayCollection > 0 ? "Total collected today" : "No collections yet"}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Pending Amount */}
                        <div className="col-md-3">
                            <div className="card shadow rounded-4" style={{ border: "2px solid #1b255b" }}>
                                <div className="card-body p-3">
                                    <h6 className="fw-semibold d-flex align-items-center gap-2 mb-3">
                                        <Clock size={18} /> Pending Amounts
                                    </h6>
                                    <h4 className="fw-bold text-danger">
                                        {getCurrencySymbol(currentLab?.baseCurrency)}{(data?.pendingAmount || 0)?.toLocaleString()}
                                    </h4>
                                    <span className="badge bg-warning text-dark">Requires Attention</span>
                                </div>
                            </div>
                        </div>

                        {/* Pending Reports */}
                        <div className="col-md-3">
                            <div className="card shadow rounded-4" style={{ border: "2px solid #1b255b" }}>
                                <div className="card-body p-3">
                                    <h6 className="fw-semibold d-flex align-items-center gap-2 mb-3">
                                        <TestTube size={18} /> Pending Reports
                                    </h6>
                                    <h4 className="fw-bold text-warning">{data?.pendingReports || 0}</h4>
                                    <p className="small text-muted mb-0">Reports in progress</p>
                                </div>
                            </div>
                        </div>

                        {/* Completed Reports */}
                        <div className="col-md-3">
                            <div className="card shadow rounded-4" style={{ border: "2px solid #1b255b" }}>
                                <div className="card-body p-3">
                                    <h6 className="fw-semibold d-flex align-items-center gap-2 mb-3">
                                        <FileCheck size={18} /> Completed Reports
                                    </h6>
                                    <h4 className="fw-bold text-success">{data?.completedReports || 0}</h4>
                                    <p className="small text-muted mb-0">Delivered successfully</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts */}
                <div className="col-md-6 mt-0">
                    <div className="card shadow rounded-4 p-3">
                        <h5 className="fw-semibold mb-4 tw-text-lg">Daily Collection</h5>
                        {series.length > 0 ? (
                            <Chart
                                options={chartOptions}
                                series={series}
                                type="line"
                                height={350}
                            />
                        ) : (
                            <div className="text-center text-muted py-5">No collection data available</div>
                        )}
                    </div>
                </div>

                <div className="col-md-6 mt-0">
                    <div className="card shadow rounded-4 p-3">
                        <h5 className="fw-semibold mb-4 tw-text-lg">Daily Sample Collection</h5>
                        {sampleAmt.length > 0 ? (
                            <Chart
                                options={chartOptionsSample}
                                series={seriesSample}
                                type="line"
                                height={350}
                            />
                        ) : (
                            <div className="text-center text-muted py-5">No sample data available</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Top Tests & Packages */}
            <div className="row g-4">
                <div className="col-lg-6">
                    <div className="card shadow rounded-4">
                        <div className="card-body p-3">
                            <h5 className="fw-semibold d-flex align-items-center gap-2 mb-3 tw-text-lg">
                                <i className="fa fa-flask" aria-hidden="true"></i> Most Ordered Tests
                            </h5>
                            {data?.topTests?.length > 0 ? (
                                data.topTests.map((test, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-3 border rounded p-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{ width: "30px", height: "30px" }}>
                                                {index + 1}
                                            </div>
                                            <span className="fw-semibold tw-text-sm">{test?.name}</span>
                                        </div>
                                        <span className="badge bg-primary tw-text-xs">
                                            {test?.count} {test?.count === 1 ? "time" : "times"}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted">No test data available</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-lg-6">
                    <div className="card shadow rounded-4">
                        <div className="card-body p-3">
                            <h5 className="fw-semibold mb-3 tw-text-lg">
                                <i className="bi bi-boxes"></i> Most Popular Packages
                            </h5>
                            {data?.topPackages?.length > 0 ? (
                                data.topPackages.map((pkg, index) => (
                                    <div key={index} className="d-flex justify-content-between align-items-center mb-3 border rounded p-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                                style={{ width: "30px", height: "30px" }}>
                                                {index + 1}
                                            </div>
                                            <span className="fw-semibold tw-text-sm">{pkg?.packageName}</span>
                                        </div>
                                        <span className="badge bg-primary tw-text-xs">
                                            {pkg?.count} {pkg?.count === 1 ? "time" : "times"}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <p className="text-muted">No package data available</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Top  10 suggestion */}
            {currentLab.isHospitalConnected == true && (
                <div className="card shadow rounded-4 p-3">

                    <h5 className="fw-semibold mb-3 tw-text-lg">Top 10 Suggestions</h5>
                    <SuggestionTable
                        suggestions={suggestions}
                        loading={suggestionsLoading}
                        showText={false}
                        showAddTest={true}
                    />
                </div>
            )}
        </div>
    );
}